/* ReportButton — small flag overlay shown to ANY logged-in user (except the
 * content owner) on recipe / cookbook cards in the public directory and home
 * peek. Opens a modal asking for a reason, posts to /api/reports.
 *
 * Sits just below the AdminModerationButtons bin (when both visible), so admins
 * see [flag] + [bin] stacked top-right.
 */
import { useState } from "react";
import { Flag, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ReportButton({ kind, id, ownerId }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Hide for: anonymous users, the content owner themselves, and admins (who already have the bin)
  if (!user) return null;
  if (ownerId && ownerId === user.user_id) return null;
  // Admins see the bin — flag is redundant for them
  if (user.role === "Admin" || user.role === "SuperAdmin") return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (reason.trim().length < 3) {
      toast.error("Please add a few words explaining what's wrong");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reports", { target_kind: kind, target_id: id, reason: reason.trim() });
      if (data.already_reported) {
        toast.info("You already reported this — admins will review it soon.");
      } else {
        toast.success("Report sent — admins will review it shortly. Thanks for flagging.");
      }
      setOpen(false);
      setReason("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit the report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={`report-${kind}-${id}`}
        title={`Report this ${kind}`}
        className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-rf-ink/60 hover:bg-rf-ink/90 text-white shadow-md flex items-center justify-center transition-transform hover:scale-110"
      >
        <Flag size={14} weight="fill" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-rf-ink/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl"
            data-testid="report-modal"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"><Flag size={20} weight="fill" className="text-red-600" /></div>
                <h2 className="font-display font-black text-rf-ink text-lg">Report this {kind}</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-rf-ink-soft hover:text-rf-ink" data-testid="report-modal-close">
                <X size={20} weight="bold" />
              </button>
            </div>
            <p className="text-sm text-rf-ink-soft mb-3">
              Tell our admins what&apos;s wrong (spam, copyright, offensive content, etc.). We&apos;ll review within 24 hours.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="report-reason"
              rows={4}
              maxLength={1000}
              required
              minLength={3}
              placeholder="Briefly describe the problem…"
              className="w-full px-3 py-2 rounded-lg border border-rf-ink/15 bg-white text-rf-ink text-sm focus:outline-none focus:border-rf-orange resize-y"
            />
            <p className="text-[11px] text-rf-ink-soft text-right mt-1">{reason.length}/1000</p>
            <div className="mt-3 flex gap-2 justify-end">
              <button type="button" onClick={() => setOpen(false)} className="btn-outline-ink !py-2 !px-3 text-sm">Cancel</button>
              <button type="submit" disabled={submitting || reason.trim().length < 3} data-testid="report-submit" className="btn-amber !py-2 !px-3 text-sm disabled:opacity-50">
                {submitting ? "Sending…" : "Send report"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
